import React, { memo, useEffect, useState } from "react";
import { TenantSummaryStepProps } from "./types";
import { OfficeRow } from "services/api/offices/types";
import { TenantUserRow } from "services/api/tenants/types";
import { getOfficesAPI } from "services/api/offices/api";
import { searchTenantUsersAPI } from "services/api/tenants/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { ReactSVG } from "react-svg";
import TenantSubscriptionSummary from "./components/TenantSubscriptionSummary/TenantSubscriptionSummary";

const TenantSummaryStep: React.FC<TenantSummaryStepProps> = ({
  formValues,
  subscriptionValues,
  existingSubscription,
}) => {
  const tenantId = formValues.id;
  const [users, setUsers] = useState<TenantUserRow[]>([]);
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (tenantId) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchAll = async () => {
    if (!tenantId) return;
    setIsLoading(true);
    try {
      const [usersRes, officesRes] = await Promise.all([
        searchTenantUsersAPI(tenantId, initialDataWithPagination.pagination),
        getOfficesAPI(initialDataWithPagination.pagination, null, tenantId),
      ]);
      setUsers(usersRes.data);
      setOffices(officesRes.data);
    } catch {
      // error handled by snackbar
    }
    setIsLoading(false);
  };

  const displayName =
    formValues.tenantTypeId === "P"
      ? [formValues.firstName, formValues.lastName].filter(Boolean).join(" ")
      : formValues.companyName;

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Pregled tenanta
        </h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Provjerite unesene podatke prije spremanja tenanta.
        </p>

        {isLoading ? (
          <Loader isLoading={true}> </Loader>
        ) : (
          <div className="border border-slate-200">
            {/* Osnovni podaci */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG
                    src="/media/icons/company.svg"
                    className="w-5 h-5 stroke-slate-600"
                  />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Osnovni podaci
                  </h3>
                  <p className="text-xs text-slate-500">
                    Pregled osnovnih informacija o tenantu
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-sm">
                <div>
                  <span className="text-slate-400">Naziv:</span>
                  <span className="ml-2 text-slate-700 font-medium">
                    {displayName || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Email:</span>
                  <span className="ml-2 text-slate-700 font-medium">
                    {formValues.email || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">OIB:</span>
                  <span className="ml-2 text-slate-700 font-medium">
                    {formValues.personalIdentificationNumber || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Telefon:</span>
                  <span className="ml-2 text-slate-700 font-medium">
                    {formValues.phoneNumber || "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Korisnici */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG
                    src="/media/icons/users.svg"
                    className="w-5 h-5 stroke-slate-600"
                  />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Korisnici{" "}
                    <span className="text-xs text-slate-400 font-normal">
                      ({users.length})
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Korisnici povezani s tenantom
                  </p>
                </div>
              </div>
              {users.length === 0 ? (
                <p className="text-sm text-slate-400">Nema povezanih korisnika</p>
              ) : (
                <div className="space-y-2">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <span>
                        {u.firstName} {u.lastName}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pretplata */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG
                    src="/media/icons/company.svg"
                    className="w-5 h-5 stroke-slate-600"
                  />
                </div>
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Pretplata
                  </h3>
                  <p className="text-xs text-slate-500">
                    Što se potpisuje sa spremanjem
                  </p>
                </div>
              </div>

              <TenantSubscriptionSummary
                tenantId={formValues.id}
                isPerson={formValues.tenantTypeId === "P"}
                subscriptionValues={subscriptionValues}
                existingSubscription={existingSubscription}
              />
            </div>

            {/* Poslovnice */}
            <div className="py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG
                    src="/media/icons/company.svg"
                    className="w-5 h-5 stroke-slate-600"
                  />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Poslovnice{" "}
                    <span className="text-xs text-slate-400 font-normal">
                      ({offices.length})
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Poslovnice povezane s tenantom
                  </p>
                </div>
              </div>
              {offices.length === 0 ? (
                <p className="text-sm text-slate-400">Nema dodanih poslovnica</p>
              ) : (
                <div className="space-y-2">
                  {offices.map((o) => (
                    <div
                      key={o.id}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <span>{o.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(TenantSummaryStep);
